import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { map } from 'rxjs';

import { AuthService } from '@services/auth.service';
import { ToastService } from '@services/toast.service';

import type { AppPermission } from '@models/auth.model';

import type { CanActivateFn } from '@angular/router';

/**
 * Guard to protect routes based on permissions.
 * Usage in routes:
 * {
 *   path: '...',
 *   canActivate: [permissionGuard],
 *   data: { permissions: [AppPermission.X, AppPermission.Y], logicalOp: 'AND' }
 * }
 */
export const permissionGuard: CanActivateFn = (route, _state) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const toastService = inject(ToastService);

  return authService.checkToken().pipe(
    map((isValid) => {
      if (!isValid) {
        authService.forceLogout();
        return false;
      }

      const requiredPermissions = route.data['permissions'] as AppPermission[] | undefined;
      const logicalOp = (route.data['logicalOp'] as 'AND' | 'OR') || 'OR';

      if (!requiredPermissions || requiredPermissions.length === 0) {
        return true;
      }

      const hasPerm =
        logicalOp === 'OR'
          ? authService.hasAnyPermission(requiredPermissions)
          : authService.hasAllPermissions(requiredPermissions);

      if (hasPerm) {
        return true;
      }

      toastService.error('Accesso negato', 'Non hai i permessi necessari per accedere a questa pagina');
      void router.navigate(['/account/modifica-password']);
      return false;
    }),
  );
};
